import { clsx } from "clsx";
import { type CSSProperties, forwardRef, Fragment, type PropsWithChildren, useEffect, useRef, useState } from "react";
import type { AriaTabListProps } from "react-aria";

import { useResizeObserver } from "../../hooks.ts";
import { useTabList } from "../../hooks/useTabList.tsx";
import { ActionButton } from "../ActionButton/ActionButton.tsx";
import { ActionButtonIntent, ActionButtonSize } from "../ActionButton/settings.ts";
import type { GridCard } from "../CardGrid/CardGrid.tsx";
import { HeaderBar, type HeaderBarProps } from "../HeaderBar/HeaderBar.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { type TabItemElement, TabListBar } from "../Tabs/TabListBar.tsx";
import { TabPanel } from "../Tabs/TabPanel.tsx";
import style from "./Card.module.css";

export interface TabCardProps
  extends Omit<HeaderBarProps, "className" | "style" | "domTitleProps">,
    GridCard,
    AriaTabListProps<TabItemElement> {
  className?: string;
  style?: CSSProperties;
  contentClassName?: string;
  shrinkable?: boolean;
  scrollable?: boolean;
  noPadding?: boolean;
  fillHeight?: boolean;

  // For tabs
  keepAlive?: boolean;
}

const INFO_CARD_SHRINK_MIN = 400;

const TabCard = forwardRef<HTMLDivElement, PropsWithChildren<TabCardProps>>(function TabCard(props, ref) {
  const { ref: tabRef, state, tabListProps } = useTabList<HTMLDivElement, TabItemElement>(props);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isShrank, setShrink] = useState(props.shrinkable);
  const [isShrinkable, setShrinkable] = useState(props.shrinkable);
  const contentBoxSize = useResizeObserver(contentRef);

  useEffect(() => {
    const shrinkable = props.shrinkable && (contentBoxSize?.height ?? 0) > INFO_CARD_SHRINK_MIN;
    setShrinkable(shrinkable);
  }, [contentBoxSize, props.shrinkable]);

  return (
    <div
      ref={ref}
      className={clsx(style.card, props.className, { [style.card_fill]: props.fillHeight })}
      data-testid={"tab-card"}
      {...{ "data-e2e-shrink": isShrinkable ? String(isShrank) : undefined }}
      style={props.style}
    >
      {(!!props.title || !!props.actions) && (
        <HeaderBar
          title={props.title}
          subtitle={props.subtitle}
          tag={props.tag}
          actions={props.actions}
          icon={props.icon ? <Icon icon={props.icon} /> : undefined}
          className={style.header_bar}
        />
      )}
      <TabListBar state={state} tabListProps={tabListProps} ref={tabRef} />
      <div
        className={clsx(style.body, {
          [style.scrollable_body]: props.scrollable,
        })}
        style={{ maxHeight: isShrank ? INFO_CARD_SHRINK_MIN : undefined }}
      >
        <div
          ref={contentRef}
          className={clsx(style.body_content, props.contentClassName)}
          style={{ height: isShrank ? "unset" : undefined, padding: props.noPadding === true ? 0 : undefined }}
          data-testid={"card-content"}
        >
          {props.keepAlive ? (
            <TabPanel state={state} keepAlive>
              {(children) => children.map((child) => <Fragment key={child.key}>{child.children}</Fragment>)}
            </TabPanel>
          ) : (
            <TabPanel state={state} key={state.selectedKey}>
              {({ children, key }) => <Fragment key={key}>{children}</Fragment>}
            </TabPanel>
          )}
        </div>
      </div>
      {!!isShrinkable && (
        <div className={clsx(style.footer, { [style.footer_shadow]: isShrank })} data-testid={"card-shrink"}>
          <ActionButton
            onPress={() => {
              setShrink(!isShrank);
            }}
            label={`${isShrank ? "Expand" : "Shrink"} the Card`}
            intent={ActionButtonIntent.Secondary}
            size={ActionButtonSize.InTableCell}
          />
        </div>
      )}
    </div>
  );
});
export { TabCard };
